<template>
  <div :class="{ 'agent-select-wrapper': iconMode && useVuetify && !multi }">
    <!-- Icon mode for Vuetify only -->
    <template v-if="iconMode && useVuetify && !multi">
      <v-menu
        v-model="iconModeMenuOpen"
        :close-on-content-click="false"
        location="bottom end"
      >
        <template v-slot:activator="{ props }">
          <!-- No agent selected -->
          <v-tooltip
            v-if="!selectedValues"
            :text="iconModeTooltip || placeholder || 'Select Agent'"
            location="top"
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{ ...props, ...tooltipProps }"
                icon="mdi-account-plus"
                size="small"
                variant="text"
              ></v-btn>
            </template>
          </v-tooltip>
          <!-- show profile pic and name in pill -->
          <v-chip
            v-else
            v-bind="props"
            class="agent-select-pill"
            variant="outlined"
            size="small"
            :height="32"
          >
            <v-avatar start size="28">
              <v-img
                v-if="
                  selectedAgentData &&
                  selectedAgentData.users_meta &&
                  selectedAgentData.users_meta.profilePhoto
                "
                :src="getAgentPhotoUrl(selectedAgentData)"
                :alt="`${selectedAgentData.first_name} ${selectedAgentData.last_name}`"
                cover
              >
                <template v-slot:error>
                  <v-icon size="16">mdi-account</v-icon>
                </template>
              </v-img>
              <v-icon v-else size="16">mdi-account</v-icon>
            </v-avatar>
            {{
              selectedAgentData
                ? `${selectedAgentData.first_name} ${selectedAgentData.last_name}`
                : ''
            }}
          </v-chip>
        </template>
        <v-list density="compact">
          <div class="px-3 py-2">
            <v-text-field
              v-model="iconModeSearch"
              placeholder="Search agents..."
              density="compact"
              variant="outlined"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              @input="filterIconModeAgents"
            ></v-text-field>
          </div>
          <v-divider></v-divider>

          <v-list-item
            v-if="
              allowAll &&
              (!iconModeSearch || 'all'.includes(iconModeSearch.toLowerCase()))
            "
            :value="0"
            @click="selectAgentFromMenu(0)"
          >
            <v-list-item-title>{{
              allLabel || 'All Agents'
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-for="agent in filteredIconModeAgents"
            :key="agent.id"
            :value="agent.id"
            @click="selectAgentFromMenu(agent.id)"
          >
            <template v-slot:prepend>
              <v-avatar size="32">
                <v-img
                  v-if="agent.users_meta && agent.users_meta.profilePhoto"
                  :src="getAgentPhotoUrl(agent)"
                  :alt="`${agent.first_name} ${agent.last_name}`"
                  cover
                ></v-img>
                <v-icon v-else size="20">mdi-account</v-icon>
              </v-avatar>
            </template>
            <v-list-item-title
              >{{ agent.first_name }} {{ agent.last_name }}</v-list-item-title
            >
          </v-list-item>
          <v-list-item
            v-if="filteredIconModeAgents.length === 0 && iconModeSearch"
          >
            <v-list-item-title class="text-grey text-center">
              No agents found
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
    <template v-else-if="autocomplete">
      <div v-if="!multi && !useVuetify" class="typeahead-container">
        <el-autocomplete
          v-model="searchQuery"
          :placeholder="placeholder || 'Type Agent Name'"
          :fetch-suggestions="filterAgents"
          @select="selectAgent"
          @clear="doClear"
          clearable
          :size="size"
          style="width: 100%"
        >
          <template #default="{ item }">
            <div>{{ item.first_name }} {{ item.last_name }}</div>
          </template>
        </el-autocomplete>
      </div>
      <div v-else-if="!multi && useVuetify" class="typeahead-container">
        <v-text-field
          v-model="searchQuery"
          :label="placeholder || 'Type Agent Name'"
          @input="onSearchInput"
          :density="vuetifyDensity"
          variant="outlined"
          :block="block"
          :loading="loading"
          clearable
          @click:clear="doClear"
        >
          <template v-slot:append-inner v-if="loading">
            <v-progress-circular
              indeterminate
              color="primary"
              size="20"
            ></v-progress-circular>
          </template>
        </v-text-field>

        <!-- Results dropdown for autocomplete -->
        <div
          v-if="showResults && filteredAgents.length > 0"
          class="results-dropdown"
        >
          <div
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="result-item"
            @click="selectAgent(agent)"
          >
            {{ agent.first_name }} {{ agent.last_name }}
          </div>
        </div>
      </div>
      <div v-else>
        <v-alert type="error" variant="tonal" density="compact">
          Can't use multi-select with autocomplete
        </v-alert>
      </div>
    </template>
    <template v-else>
      <el-select
        v-if="!useVuetify"
        v-model="selectedValues"
        @change="doChange"
        @clear="doClear"
        :placeholder="
          placeholder || (multi ? 'Select Agents' : 'Select an Agent')
        "
        :size="size"
        :multiple="multi"
        :style="`display:${display}; width: ${width};`"
        :filterable="true"
      >
        <el-option
          v-if="allowAll"
          :value="0"
          :label="allLabel || 'All Agents'"
        ></el-option>
        <el-option
          v-for="option in getAgents"
          :key="option.id"
          :value="option.id"
          :label="`${option.first_name} ${option.last_name}`"
        ></el-option>
      </el-select>
      <v-select
        v-else
        v-model="selectedValues"
        @update:model-value="doChange"
        @click:clear="doClear"
        :label="label || (multi ? 'Select Agents' : 'Select an Agent')"
        :items="selectOptions"
        :multiple="multi"
        :density="vuetifyDensity"
        :placeholder="placeholder"
        variant="outlined"
        :style="getSelectStyle()"
        hide-details="auto"
        :clearable="clearable"
      ></v-select>
    </template>
  </div>
</template>

<script>
import { isProxy, toRaw } from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'agentSelect',
  computed: {
    ...mapGetters('agents', ['getAgent', 'getAgents']),
    ...mapGetters('auth', ['getProfilePhotoURL']),
    selectedAgentData() {
      if (!this.selectedValues || this.selectedValues === 0) {
        return null;
      }
      return this.getAgents.find((agent) => agent.id === this.selectedValues);
    },
    filteredIconModeAgents() {
      if (!this.iconModeSearch) {
        return this.getAgents || [];
      }

      const search = this.iconModeSearch.toLowerCase();
      return (this.getAgents || []).filter((agent) => {
        const fullName = `${agent.first_name} ${agent.last_name}`.toLowerCase();
        return fullName.includes(search);
      });
    },
    selectOptions() {
      const options = [];
      if (this.allowAll) {
        options.push({
          value: 0,
          title: this.allLabel ? this.allLabel : 'All Agents',
        });
      }
      if (this.getAgents && this.getAgents.length > 0) {
        this.getAgents.forEach((agent) => {
          options.push({
            value: agent.id,
            title: `${agent.first_name} ${agent.last_name}`,
          });
        });
      }
      return options;
    },
    vuetifyDensity() {
      if (this.density) {
        return this.density;
      }
      const densityMap = {
        default: 'default',
        small: 'compact',
        large: 'comfortable',
      };
      return densityMap[this.size] || 'compact';
    },
  },
  props: {
    useVuetify: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconModeTooltip: {
      type: String,
      required: false,
    },
    values: {
      type: Array,
      required: true,
    },
    multi: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    allowAll: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    allLabel: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      required: false,
      default: () => {
        return 'small';
      },
    },
    density: {
      type: String,
      required: false,
      validator: (value) =>
        ['comfortable', 'default', 'compact'].includes(value),
    },
    // autocomplete only works for a single agent
    autocomplete: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    placeholder: {
      type: String,
      required: false,
    },
    block: {
      type: Boolean,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    width: {
      type: String,
      required: false,
      default: () => {
        return '100%';
      },
    },
    display: {
      type: String,
      required: false,
      default: () => {
        return 'block';
      },
    },
  },
  emits: ['update:values', 'change'],
  data() {
    return {
      selectedValues: [],
      searchQuery: '',
      showResults: false,
      filteredAgents: [],
      loading: false,
      debounceTimeout: null,
      selectedAgent: null,
      iconModeSearch: '',
      iconModeMenuOpen: false,
    };
  },
  async created() {
    if (!this.multi) {
      this.selectedValues = undefined;
    }
    if (!this.getAgents || this.getAgents.length == 0) {
      this.loading = true;
      await this.fetchAgents(0);
      this.loading = false;
    }
    if (this.values) {
      if (!this.multi) {
        this.selectedValues = this.values.length ? this.values[0] : undefined;

        if (this.autocomplete && this.values.length) {
          const agent = this.getAgents.find((a) => a.id === this.values[0]);
          if (agent) {
            this.searchQuery = `${agent.first_name} ${agent.last_name}`;
            this.selectedAgent = agent;
          }
        }
      } else {
        this.selectedValues = this.values.slice();
      }
    }
  },
  watch: {
    values: {
      immediate: true,
      handler(n) {
        // Convert to/from array as needed
        if (!this.multi) {
          if (isProxy(n)) {
            // Vue 3 proxy converts arrays to objects which break comparisons
            const vals = toRaw(n);
            this.selectedValues = vals.length ? vals[0] : undefined;
          } else {
            this.selectedValues = n.length ? n[0] : undefined;

            const agent = toRaw(this.getAgents).find(({ id }) => id == n[0]);
            if (agent && this.autocomplete) {
              this.searchQuery = `${agent.first_name} ${agent.last_name}`;
              this.selectedAgent = agent;
            }
          }
        } else {
          this.selectedValues = n;
        }
      },
    },
  },
  mounted() {
    if (this.autocomplete && this.useVuetify) {
      document.addEventListener('click', this.handleClickOutside);
    }
  },
  beforeUnmount() {
    if (this.autocomplete && this.useVuetify) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  },
  methods: {
    ...mapActions('agents', ['fetchAgents']),
    doClear() {
      this.selectedValues = undefined;
      this.searchQuery = '';
      this.selectedAgent = null;
      this.doChange();
    },
    doChange() {
      if (!this.multi) {
        this.$emit(
          'update:values',
          this.selectedValues ? [this.selectedValues] : []
        );
        this.$emit('change', this.selectedValues ? [this.selectedValues] : []);
      } else {
        this.$emit('update:values', this.selectedValues);
        this.$emit('change', this.selectedValues);
      }
    },
    handleClickOutside(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.showResults = false;
      }
    },
    onSearchInput() {
      this.showResults = true;

      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      if (this.selectedAgent) {
        const fullName =
          this.selectedAgent.first_name + ' ' + this.selectedAgent.last_name;
        if (this.searchQuery !== fullName) {
          this.selectedAgent = null;
          this.selectedValues = undefined;
          this.$emit('update:values', []);
          this.$emit('change', []);
        }
      }

      this.debounceTimeout = setTimeout(() => {
        this.filterAgents(this.searchQuery);
      }, 300);
    },
    filterAgents(input, cb) {
      if (typeof input === 'string') {
        if (!input || input.length < 1) {
          this.filteredAgents = [];
          this.loading = false;
          return cb ? cb([]) : [];
        }

        this.loading = true;

        const query = input.toLowerCase();
        const filteredAgents = this.getAgents.filter(
          (agent) =>
            agent.first_name.toLowerCase().includes(query) ||
            agent.last_name.toLowerCase().includes(query)
        );

        this.loading = false;

        if (cb) {
          cb(filteredAgents);
        } else {
          this.filteredAgents = filteredAgents;
        }
      } else {
        // For element plus
        const query = input.query.toLowerCase();
        const filteredAgents = this.getAgents.filter(
          (agent) =>
            agent.first_name.toLowerCase().includes(query) ||
            agent.last_name.toLowerCase().includes(query)
        );

        cb(filteredAgents);
      }
    },
    selectAgent(agent) {
      const selectedAgent = agent.id ? agent : agent.item;

      this.selectedAgent = selectedAgent;
      this.searchQuery = `${selectedAgent.first_name} ${selectedAgent.last_name}`;
      this.showResults = false;
      this.selectedValues = selectedAgent.id;
      this.$emit('update:values', [selectedAgent.id]);
      this.$emit('change', [selectedAgent.id]);
    },
    selectAgentFromMenu(agentId) {
      this.selectedValues = agentId === 0 ? undefined : agentId;
      this.doChange();
      this.iconModeSearch = ''; // Clear search
      this.iconModeMenuOpen = false;
    },
    filterIconModeAgents() {
      if (this.iconModeSearch && this.selectedValues) {
        this.selectedValues = undefined;
        this.doChange();
      }
    },
    getAgentPhotoUrl(agent) {
      if (agent && agent.users_meta && agent.users_meta.profilePhoto) {
        return this.getProfilePhotoURL + agent.users_meta.profilePhoto;
      }
      return null;
    },
    getSelectStyle() {
      return this.block ? 'display:block' : '';
    },
  },
};
</script>
<style lang="scss" scoped>
.typeahead-container {
  position: relative;
  width: 100%;
}

.results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: -1px;
}

.result-item {
  padding: 10px 15px;
  cursor: pointer;
  text-align: left;
}

.result-item:hover {
  background-color: #f5f7fa;
}

.agent-select-pill {
  cursor: pointer;
  font-size: 0.875rem;
  padding-right: 12px !important;
  padding-left: 2px !important;
  background-color: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.04);
  display: inline-flex;
  align-items: center;
}

.agent-select-pill:hover {
  background-color: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.08);
}

:deep(.v-chip__content) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.agent-select-wrapper {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  height: 40px;
}

.agent-select-wrapper .v-btn {
  margin: 0;
}

.agent-select-wrapper .agent-select-pill {
  margin: 0;
  vertical-align: middle;
}
</style>
